import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { Keyboard } from 'react-native';
import { BottomSheetModal } from '@gorhom/bottom-sheet';
import { useAppDispatch, useAppSelector } from '../../redux/helper';
import { ModalContext } from '../../Context/modalContext';
import CustomBottomSheet from '../Reusables/bottomSheet/CustomBottomSheet';
import useAuth from '../../hooks/useAuth';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import {
  fetchAssignableStaffForLocation,
  selectAssignableStaffForLocation,
} from '../../redux/assignableStaffSlice';
import { selectMergedCustomers } from '../../redux/OrdersSlice';
import { isEmployeeWaiterRole } from '../../utils/roles';
import { formatMoney, replaceCurrencyString } from '../../utils/libs';
import {
  choiceName,
  describeChoice,
  pluralize,
  type PickerCustomer,
} from '../../utils/customerPicker';
import OrderCustomerPicker from '../saveOrder/OrderCustomerPicker';
import PickerFooterButton from '../saveOrder/PickerFooterButton';
import type { StaffAssignModel } from '../saveOrder/StaffAssignSection';

interface SaveOrderBottomSheetProps {
  SendOrder: () => void;
  isloading: boolean;
  /** "3 items · Ksh 1,250" under the title; read from the cart when not given. */
  summary?: string;
  /** Shown instead of saving a customer (demo mode). */
  addCustomerUnavailableMessage?: string;
}

const SNAP_POINTS = ['94%'];

/**
 * The Save Order sheet: one list of walk in, your customers and phone
 * contacts, staff to assign, and a save button that names who the order is
 * for. The chosen customer lives in the cart (ModalContext), so the basket's
 * customer bar shows it as soon as it is tapped.
 */
const SaveOrderBottomSheet = ({
  SendOrder,
  isloading,
  summary,
  addCustomerUnavailableMessage,
}: SaveOrderBottomSheetProps) => {
  const {
    visibleName,
    setVisibleName,
    input,
    setInput,
    assignedStaffId,
    setAssignedStaffId,
    selectedCustomerId,
    setSelectedCustomerId,
  } = useContext(ModalContext);
  const sheetRef = useRef<BottomSheetModal | null>(null);
  const dispatch = useAppDispatch();
  const { auth } = useAuth();
  const axiosprivate = useAxiosPrivate();
  const customers = useAppSelector(selectMergedCustomers) as PickerCustomer[];
  const cartItemCount = useAppSelector(state => state.cart.cartItems.length);
  const cartTotalAmount = useAppSelector(state => state.cart.cartTotalAmount);
  const currency = useAppSelector(
    state =>
      state.location.currentLocation?.bp_location_settings
        ?.bp_location_currency || 'KES',
  );
  const currentLocation = useAppSelector(
    state => state.location.currentLocation,
  );
  const locationId = String(currentLocation?._id || '');
  const allowStaffOrderAssignmentSetting = useAppSelector(
    state => state.settings.settings.allow_staff_order_assignment,
  );
  const employee = useAppSelector(
    state => state.auth.inApp.account.employeeInfo,
  );
  const isWaiter = isEmployeeWaiterRole(employee?.bp_employee_role);
  const assignableStaff = useAppSelector(state =>
    selectAssignableStaffForLocation(state, locationId),
  );

  const isStaffAssignmentEnabled = useMemo(() => {
    const fromLocation =
      currentLocation?.bp_location_settings
        ?.bp_location_allow_staff_order_assignment;
    if (typeof fromLocation === 'boolean') return fromLocation;
    return Boolean(allowStaffOrderAssignmentSetting);
  }, [
    currentLocation?.bp_location_settings
      ?.bp_location_allow_staff_order_assignment,
    allowStaffOrderAssignmentSetting,
  ]);

  const orderSummary =
    summary ??
    `${pluralize(cartItemCount, 'item')} · ${replaceCurrencyString(
      'Ksh',
      currency,
    )} ${formatMoney(cartTotalAmount)}`;

  useEffect(() => {
    if (visibleName) {
      sheetRef.current?.present();
      return;
    }
    sheetRef.current?.dismiss();
  }, [visibleName]);

  const loadStaff = useCallback(() => {
    if (!locationId || !auth?.accessToken) return;
    dispatch(
      fetchAssignableStaffForLocation({
        axiosinstance: axiosprivate as any,
        auth,
        locationId,
      }) as any,
    );
  }, [auth, axiosprivate, dispatch, locationId]);

  useEffect(() => {
    if (!visibleName || isWaiter || !isStaffAssignmentEnabled) return;
    if (
      assignableStaff.loading ||
      assignableStaff.featureDisabled ||
      !!assignableStaff.fetchedAt ||
      !!assignableStaff.error
    ) {
      return;
    }
    loadStaff();
  }, [
    visibleName,
    isWaiter,
    isStaffAssignmentEnabled,
    assignableStaff.loading,
    assignableStaff.featureDisabled,
    assignableStaff.fetchedAt,
    assignableStaff.error,
    loadStaff,
  ]);

  const closeSheet = useCallback(() => {
    setVisibleName(false);
    Keyboard.dismiss();
  }, [setVisibleName]);

  const handleSheetChanges = useCallback(
    (index: number) => {
      if (index !== -1) return;
      if (isloading) {
        sheetRef.current?.present();
        return;
      }
      closeSheet();
    },
    [closeSheet, isloading],
  );

  const choice = useMemo(
    () => describeChoice(input, selectedCustomerId, customers),
    [input, selectedCustomerId, customers],
  );
  const chosenName = choiceName(choice);

  const staff: StaffAssignModel | null = useMemo(() => {
    if (isWaiter) {
      return {
        members: [],
        loading: false,
        error: null,
        assignedStaffId: null,
        onToggle: () => {},
        onRetry: () => {},
        selfAssignedName:
          String(employee?.bp_employee_name || '').trim() || 'you',
      };
    }
    if (!isStaffAssignmentEnabled || assignableStaff.featureDisabled) {
      return null;
    }
    return {
      members: assignableStaff.staff,
      loading: assignableStaff.loading,
      error: assignableStaff.error,
      assignedStaffId,
      onToggle: id => setAssignedStaffId(prev => (prev === id ? null : id)),
      onRetry: loadStaff,
    };
  }, [
    isWaiter,
    employee?.bp_employee_name,
    isStaffAssignmentEnabled,
    assignableStaff.featureDisabled,
    assignableStaff.staff,
    assignableStaff.loading,
    assignableStaff.error,
    assignedStaffId,
    setAssignedStaffId,
    loadStaff,
  ]);

  const renderFooter = useCallback(
    ({ busyName }: { busyName: string | null }) => {
      const canSave = choice.kind !== 'none' && !isloading && !busyName;
      const main = busyName
        ? `Adding ${busyName}…`
        : choice.kind === 'none'
          ? 'Choose a customer to save'
          : 'Save Order';
      const who = !busyName && choice.kind !== 'none' ? chosenName : '';
      return (
        <PickerFooterButton
          label={main}
          detail={who}
          disabled={!canSave}
          loading={isloading}
          onPress={SendOrder}
          accessibilityLabel={who ? `Save order for ${who}` : main}
        />
      );
    },
    [SendOrder, choice.kind, chosenName, isloading],
  );

  return (
    <CustomBottomSheet
      bottomSheetModalRef={sheetRef}
      snapPoints={SNAP_POINTS}
      constrainWidth
      enableDynamicSizing={false}
      handleSheetChanges={handleSheetChanges}
      dismissible={!isloading}
      disableDefaultScroll>
      <OrderCustomerPicker
        variant="sheet"
        active={visibleName}
        title="Save Order"
        subtitle={orderSummary}
        onClose={isloading ? undefined : closeSheet}
        selectedName={input}
        selectedCustomerId={selectedCustomerId}
        onSelect={(name, customerId) => {
          setInput(name);
          setSelectedCustomerId(customerId);
        }}
        staff={staff}
        renderFooter={renderFooter}
        addCustomerUnavailableMessage={addCustomerUnavailableMessage}
      />
    </CustomBottomSheet>
  );
};

export default SaveOrderBottomSheet;
